import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import {
  closeIcon,
  daggerMark,
  daggerText,
  menuIcon,
  searchIcon,
} from "../../icons"
import "./header.component.css"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { daggerNavigate } from "../../utils/navigate.util"
import SiteMenuComponent from "../site-menu/site-menu.component"

const HeaderComponent = (props) => {
  const { HexColor } = props
  const [showMenu, setShowMenu] = useState()
  const [items, setItems] = useState({})
  const elRef = useRef()
  const hexRef = useRef()
  const fullMenuRef = useRef()
  const { pathname } = useLocation()

  const normalizedPath = pathname.toLowerCase().replace(/\/+$/, "").trim()
  const isHomeRoute = ["", "/", "/home"].includes(normalizedPath)

  // Close the expanded menu on route change
  useEffect(() => {
    if (fullMenuRef.current) {
      fullMenuRef.current.classList.remove("active")
    }
    setShowMenu(false)
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute("content", "#000000")
  }, [pathname])

  // Get items on header event
  useEffect(() => {
    const loadItems = () => {
      const store = window.sessionStorage.getItem("__dagger-store__")
      if (store) {
        const json = JSON.parse(store)
        setItems({ ...json.header, ...json.settings })
      }
    }

    document.addEventListener("EVENTS.HEADER_LOADED", loadItems)
    loadItems()
    return () => document.removeEventListener("EVENTS.HEADER_LOADED", loadItems)
  }, [])

  // Set hex color
  useEffect(() => {
    if (HexColor) {
      hexRef.current = HexColor
    } else {
      hexRef.current = null
    }
  }, [HexColor])

  // Setup scrolltrigger to update background on scroll
  useLayoutEffect(() => {
    const handleScrollDown = () => {
      if (elRef.current) {
        elRef.current.classList.add("activated")

        if (hexRef.current && elRef.current?.style) {
          elRef.current.style.backgroundColor = hexRef.current
        }
      }
    }

    const handleScrollUp = () => {
      if (elRef.current) {
        elRef.current.classList.remove("activated")

        if (hexRef.current && elRef.current?.style) {
          elRef.current.style.backgroundColor = "initial"
        }
      }
    }

    const trigger = ScrollTrigger.create({
      trigger: `.dagger-header`,
      start: "10% top",
      onEnter: handleScrollDown,
      onLeaveBack: handleScrollUp,
    })

    return () => trigger.kill()
  }, [])

  const handleCloseMenu = useCallback(() => {
    if (fullMenuRef.current) {
      fullMenuRef.current.classList.remove("active")
    }

    setThemeColor("#000000")

    setTimeout(() => {
      setShowMenu(false)
    }, 250)
  }, [])

  // Listen for invert event
  useEffect(() => {
    const handleInvert = (e) => {
      if (!elRef.current) return

      const invert = e.detail
      if (invert) {
        elRef.current.classList.add("inverted")
      } else {
        elRef.current.classList.remove("inverted")
      }
    }

    document.addEventListener("EVENTS.INVERT_HEADER", handleInvert)
    document.addEventListener("EVENTS.CLOSE_HEADER", handleCloseMenu)

    return () => {
      document.removeEventListener("EVENTS.INVERT_HEADER", handleInvert)
      document.removeEventListener("EVENTS.CLOSE_HEADER", handleCloseMenu)
    }
  }, [handleCloseMenu])

  const setThemeColor = (color) => {
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute("content", color)
  }

  const handleShowMenu = () => {
    setShowMenu(true)
    setThemeColor("#0000ff")
    setTimeout(() => {
      fullMenuRef.current.classList.add("active")
    })
  }

  const openContactForm = (defaultSubject) => {
    document.dispatchEvent(
      new CustomEvent("EVENTS.OPEN_CONTACT_FORM", {
        detail: { defaultSubject },
      }),
    )
  }

  const navigate = useNavigate()

  const handleShowLoader = (e, dest) => {
    if (dest === window.location.pathname) {
      e.preventDefault()
      handleCloseMenu()
      return
    }
    e.preventDefault()
    daggerNavigate(() => navigate(dest))
  }

  return (
    <header
      className={`dagger-header ${isHomeRoute ? "is-home" : "is-not-home"}`}
      ref={elRef}
    >
      <Link
        to={`/`}
        onClick={(e) => handleShowLoader(e, `/`)}
        className="dagger-top-level dagger-main-mark"
      >
        {daggerMark}
      </Link>
      <button
        className="dagger-top-level search-btn"
        onClick={() =>
          document.dispatchEvent(new CustomEvent("EVENTS.OPEN_SPOTLIGHT"))
        }
        aria-label="Search"
      >
        {searchIcon}
      </button>
      <button
        className="dagger-top-level menu-btn"
        onClick={handleShowMenu}
        aria-label="Menu"
      >
        {menuIcon}
      </button>
      {showMenu && (
        <div className="dagger-header-menu">
          <button
            className="dagger-header-menu-close-btn"
            onClick={handleCloseMenu}
            aria-label="Close menu"
          >
            {closeIcon}
          </button>
          <div className="dagger-header-menu-content" ref={fullMenuRef}>
            <div className="dagger-header-menu-content-top">
              <SiteMenuComponent items={items} />
              <div className="dagger-header-menu-content-top-extras">
                <h2>{items.ContactHeading}</h2>
                <p>
                  {items.ContactAddressLineOne}
                  <br />
                  {items.ContactAddressLineTwo}
                  <br />
                  {items.ContactAddressLineThree}
                </p>
                {items.ContactPhone && (
                  <a href={`tel:+1${items.ContactPhone.replaceAll(".", "")}`}>
                    {items.ContactPhone}
                  </a>
                )}
                <a href={`mailto:${items.ContactEmail}`}>
                  {items.ContactEmail}
                </a>
                <a
                  className="underline"
                  href="#contact"
                  onClick={(e) => {
                    e.preventDefault()
                    openContactForm("General Inquiry")
                  }}
                >
                  Contact
                </a>
                <div className="dagger-header-menu-content-top-extras-bottom">
                  {/* <a
                    href="#contact"
                    onClick={(e) => {
                      e.preventDefault()
                      openContactForm("Press")
                    }}
                  >
                    Press
                  </a> */}
                </div>
              </div>
            </div>
            <div className="dagger-header-menu-wordmark">{daggerText}</div>
          </div>
        </div>
      )}
    </header>
  )
}

export default HeaderComponent
